import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { dashboardService } from '@/lib/dashboardService'
import { useAuth } from '@/contexts/AuthContext'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { TrendingUp, X, Award, BookOpen, Users, CheckCircle2, FileText } from 'lucide-react'
import { KPIOverview } from '@/components/dashboard/KPIOverview'
import { ProgressOverview } from '@/components/dashboard/ProgressOverview'
import { AlertCenter } from '@/components/dashboard/AlertCenter'
import { SubmissionPieChart } from '@/components/dashboard/Charts/SubmissionPieChart'

export const DepartmentDashboard: React.FC = () => {
  const { user, role } = useAuth()
  const [selectedDeptId, setSelectedDeptId] = useState<string>('')
  const [allDepts, setAllDepts] = useState<any[]>([])
  const [department, setDepartment] = useState<any>(null)
  const [teachers, setTeachers] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [submissions, setSubmissions] = useState<any[]>([])
  const [sessions, setSessions] = useState<any[]>([])
  const [unlockRequests, setUnlockRequests] = useState<any[]>([])
  const [summaryData, setSummaryData] = useState<any[]>([])
  const [selectedSummaryAssignment, setSelectedSummaryAssignment] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [compStats, setCompStats] = useState<any>(null)
  const [showTeacherDetails, setShowTeacherDetails] = useState<boolean>(false)


  const loadDeptData = async () => {
    if (!user) return
    setLoading(true)
    try {
      try {
        await dashboardService.syncEnrollments()
      } catch (syncErr) {
        console.warn('Sync enrollments failed silently:', syncErr)
      }
      const depts = await dataService.getDepartments()
      setAllDepts(depts)

      let deptId: number | null = null
      const allProfiles = await dataService.getProfiles()
      
      if (role === 'admin') {
        if (selectedDeptId) {
          deptId = Number(selectedDeptId)
        } else if (depts.length > 0) {
          deptId = depts[0].id
          setSelectedDeptId(String(depts[0].id))
        }
      } else {
        const currentProfile = allProfiles.find((p: any) => p.id === user.id)
        deptId = currentProfile?.department_id || null
      }

      if (deptId !== null) {
        const [
          allSubjects,
          allClassrooms,
          allAssignments,
          allSubmissions,
          allSessions,
          allUnlockRequests
        ] = await Promise.all([
          dataService.getSubjects(),
          dataService.getClassrooms(),
          dataService.getTeachingAssignments(),
          dataService.getScoreSubmissions(),
          dataService.getAssessmentSessions(),
          dataService.getUnlockRequests()
        ])

        const activeSession = allSessions.find((s: any) => s.is_open)
        const sessionId = activeSession?.id || 1



        const summary = await dataService.getDepartmentSummary(deptId, sessionId)
        setSummaryData(summary)

        // Fetch comprehensive HOD metrics
        const comp = await dashboardService.getComprehensiveStats('head_of_department', deptId)
        setCompStats(comp)

        const currentDept = depts.find((d: any) => d.id === deptId)
        setDepartment(currentDept)

        const deptSubjects = allSubjects.filter((s: any) => s.department_id === deptId)
        setSubjects(deptSubjects)
        const deptSubjectIds = deptSubjects.map((s: any) => s.id)

        const deptAssignments = allAssignments.filter((a: any) => deptSubjectIds.includes(a.subject_id))
        setAssignments(deptAssignments)

        setTeachers(allProfiles)
        setClassrooms(allClassrooms)
        setSubmissions(allSubmissions)
        setSessions(allSessions)

        // Filter unlock requests for HOD's department
        const filteredReqs = allUnlockRequests.filter((r: any) => {
          const assign = allAssignments.find((a: any) => a.id === r.teaching_assignment_id)
          return assign && deptSubjectIds.includes(assign.subject_id)
        })
        setUnlockRequests(filteredReqs)


      }
    } catch (err) {
      console.error('Error loading HOD compliance dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDeptData()
  }, [user, selectedDeptId])

  const handleApproveUnlock = async (id: number) => {
    if (!user) return
    const success = await dataService.approveUnlockRequest(id, user.id)
    if (success) {
      setNotification({ type: 'success', message: '🔓 อนุมัติปลดล็อกคะแนนสอบเรียบร้อยแล้ว' })
      loadDeptData()
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const handleRejectUnlock = async (id: number) => {
    if (!user) return
    const success = await dataService.rejectUnlockRequest(id, user.id)
    if (success) {
      setNotification({ type: 'success', message: '❌ ปฏิเสธคำขอปลดล็อกคะแนน' })
      loadDeptData()
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const getTeacherName = (id: string) => {
    const t = teachers.find(p => p.id === id)
    return t ? `${t.first_name} ${t.last_name}` : 'ไม่ทราบชื่อครู'
  }

  const getSubjectInfo = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : 'ไม่ทราบวิชา'
  }

  const getClassroomName = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : 'ไม่ทราบห้อง'
  }

  const filteredAssigns = assignments

  const getComplianceStats = () => {
    if (filteredAssigns.length === 0) return { rate: 0, total: 0, submitted: 0, pending: 0 }
    
    let totalAssigned = filteredAssigns.length * 2
    let submittedCount = 0

    filteredAssigns.forEach(a => {
      const currentSession = sessions[0]
      if (currentSession) {
        const sub = submissions.find(s => s.teaching_assignment_id === a.id && s.assessment_session_id === currentSession.id)
        if (sub) {
          if (sub.pre_test_status === 'submitted') submittedCount++
          if (sub.post_test_status === 'submitted') submittedCount++
        }
      }
    })

    const rate = Math.round((submittedCount / totalAssigned) * 100)
    return {
      rate,
      total: totalAssigned,
      submitted: submittedCount,
      pending: totalAssigned - submittedCount
    }
  }

  const compliance = getComplianceStats()

  // Get Alerts for abnormal situations
  const getAlerts = () => {
    const alertsList: any[] = []
    
    // Alert 1: Unlock requests pending
    const pendingReqs = unlockRequests.filter(r => r.status === 'pending')
    if (pendingReqs.length > 0) {
      alertsList.push({
        id: 'hod-unlocks',
        type: 'danger',
        message: `🔓 มีคำขอปลดล็อกสิทธิ์จากครูรออนุมัติจำนวน ${pendingReqs.length} รายการ`
      })
    }

    // Alert 2: Classrooms with negative Learning Gain
    summaryData.forEach(s => {
      const assign = assignments.find(a => a.id === s.assignmentId)
      if (assign && filteredAssigns.some(fa => fa.id === assign.id)) {
        if (s.preEnteredCount > 0 && s.postEnteredCount > 0 && s.learningGain < 0) {
          alertsList.push({
            id: `hod-low-gain-${assign.id}`,
            type: 'warning',
            message: `🚨 พัฒนาการติดลบ! ในห้อง ม.${getClassroomName(assign.classroom_id)} วิชา ${getSubjectInfo(assign.subject_id)} (Gain: ${s.learningGain.toFixed(2)})`
          })
        }
      }
    })

    return alertsList
  }

  const alerts = getAlerts()





  // Get 5 recent submissions
  const getRecentSubmissions = () => {
    const deptSubjectIds = subjects.map(s => s.id)
    const mySubmissions = submissions.filter(sub => {
      const assign = assignments.find(a => a.id === sub.teaching_assignment_id)
      return assign && deptSubjectIds.includes(assign.subject_id)
    })
    return mySubmissions.slice(-5).reverse()
  }

  const recentSubmissionsList = getRecentSubmissions()

  const formatDate = (dateVal: any) => {
    if (!dateVal) return '—'
    const d = new Date(dateVal)
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('th-TH')
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }
  const pendingRequests = unlockRequests.filter((r: any) => r.status === 'pending')

  const getDeptChartData = () => {
    return subjects.slice(0, 5).map(sub => {
      const subSummaries = summaryData.filter(s => s.subjectId === sub.id)
      
      let preSum = 0, preCount = 0
      let postSum = 0, postCount = 0
      
      subSummaries.forEach(s => {
        preSum += s.preAvg * s.preEnteredCount
        preCount += s.preEnteredCount
        postSum += s.postAvg * s.postEnteredCount
        postCount += s.postEnteredCount
      })

      const preAvg = preCount > 0 ? preSum / preCount : 0
      const postAvg = postCount > 0 ? postSum / postCount : 0
      const gain = postAvg - preAvg
      const maxVal = Math.max(sub.max_score_pre, sub.max_score_post) || 10
      const prePercent = maxVal > 0 ? (preAvg / maxVal) * 100 : 0
      const postPercent = maxVal > 0 ? (postAvg / maxVal) * 100 : 0

      return {
        id: sub.id,
        label: `${sub.code}`,
        preAvg,
        postAvg,
        gain,
        prePercent,
        postPercent
      }
    })
  }

  const deptChartData = getDeptChartData()

  if (!department && role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>📊 แดชบอร์ดติดตามส่งคะแนน</h1>
        </div>
        <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>⚠️</span>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>ไม่สามารถดึงข้อมูลกลุ่มสาระฯ ได้</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '500px', margin: '0 auto 24px' }}>
            เนื่องจากบัญชีของคุณยังไม่ได้เชื่อมโยงกับกลุ่มสาระวิชาใด ๆ ในระบบ กรุณาอัปเดตกลุ่มสาระของคุณที่หน้าข้อมูลส่วนตัว หรือติดต่อผู้ดูแลระบบเพื่อแก้ไขสิทธิ์
          </p>
          <Link to="/department/profile" className="btn btn-primary">👤 ตั้งค่ากลุ่มสาระในหน้าข้อมูลส่วนตัว</Link>
        </div>
      </div>
    )
  }

  const getPieChartStats = () => {
    let complete = 0
    let inProgress = 0
    let notStarted = 0
    const currentSession = sessions[0]

    assignments.forEach(a => {
      if (currentSession) {
        const sub = submissions.find(s => s.teaching_assignment_id === a.id && s.assessment_session_id === currentSession.id)
        const hasPre = sub?.pre_test_status === 'submitted'
        const hasPost = sub?.post_test_status === 'submitted'
        if (hasPre && hasPost) {
          complete++
        } else if (hasPre || hasPost) {
          inProgress++
        } else {
          notStarted++
        }
      } else {
        notStarted++
      }
    })

    return { complete, inProgress, notStarted }
  }

  const pieStats = getPieChartStats()
  const getDistinctStudentsCount = () => {
    const distinctClassroomsMap = new Map<number, number>()
    summaryData.forEach(s => {
      if (s.classroomId) {
        const current = distinctClassroomsMap.get(s.classroomId) || 0
        distinctClassroomsMap.set(s.classroomId, Math.max(current, s.totalStudents || 0))
      }
    })
    return Array.from(distinctClassroomsMap.values()).reduce((acc, count) => acc + count, 0)
  }

  const totalStudentsCount = compStats?.overview?.totalStudents || getDistinctStudentsCount()

  // Count pre and post submitted assignments
  const preSubmittedCount = submissions.filter(s => s.pre_test_status === 'submitted' && assignments.some(a => a.id === s.teaching_assignment_id)).length
  const postSubmittedCount = submissions.filter(s => s.post_test_status === 'submitted' && assignments.some(a => a.id === s.teaching_assignment_id)).length

  // Get summary of teachers' submission status
  const getTeacherSubmissionsSummary = () => {
    const uniqueTeacherIds = Array.from(new Set(assignments.map(a => a.teacher_id)))
    return uniqueTeacherIds.map(teacherId => {
      const teacher = teachers.find(t => t.id === teacherId)
      const teacherName = teacher ? `${teacher.first_name} ${teacher.last_name}` : 'ไม่ทราบรายชื่อผู้สอน'
      
      const teacherAssigns = assignments.filter(a => a.teacher_id === teacherId)
      
      let preComplete = 0
      let postComplete = 0
      let fullyComplete = 0
      let totalStudentsTaught = 0
      let totalPreEntered = 0
      let totalPostEntered = 0

      teacherAssigns.forEach(assign => {
        const summary = summaryData.find(s => s.assignmentId === assign.id)
        if (summary) {
          const studentCount = summary.totalStudents || 0
          const preEntered = summary.preEnteredCount || 0
          const postEntered = summary.postEnteredCount || 0
          
          totalStudentsTaught += studentCount
          totalPreEntered += preEntered
          totalPostEntered += postEntered

          if (preEntered === studentCount && studentCount > 0) preComplete++
          if (postEntered === studentCount && studentCount > 0) postComplete++
          if (preEntered === studentCount && postEntered === studentCount && studentCount > 0) fullyComplete++
        }
      })

      const preRate = totalStudentsTaught > 0 ? Math.round((totalPreEntered / totalStudentsTaught) * 100) : 0
      const postRate = totalStudentsTaught > 0 ? Math.round((totalPostEntered / totalStudentsTaught) * 100) : 0

      return {
        teacherId,
        teacherName,
        classroomsCount: teacherAssigns.length,
        preComplete,
        postComplete,
        fullyComplete,
        totalStudentsTaught,
        preRate,
        postRate,
        allComplete: fullyComplete === teacherAssigns.length && teacherAssigns.length > 0
      }
    })
  }

  const teacherSummaryList = getTeacherSubmissionsSummary()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Department Selector for Admin */}
      {role === 'admin' && allDepts.length > 0 && (
        <div className="glassmorphism" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>
            📂 สลับกลุ่มสาระการเรียนรู้:
          </label>
          <select
            value={selectedDeptId}
            onChange={(e) => setSelectedDeptId(e.target.value)}
            className="input-field"
            style={{ width: '280px', margin: 0 }}
          >
            {allDepts.map((d: any) => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
            📊 แดชบอร์ดติดตามส่งคะแนน กลุ่มสาระฯ{department?.name}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>ติดตามสถานะการส่งคะแนนสอบและอนุมัติคำร้องปลดล็อกการแก้ไขคะแนนของครูในกลุ่มสาระ</p>
        </div>
        <Link to="/department/reports" className="btn btn-primary">📈 รายงานสถิติผลสัมฤทธิ์</Link>
      </div>

      {notification && (
        <div style={{
          background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${notification.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
          borderRadius: '8px',
          padding: '16px',
          color: notification.type === 'success' ? 'var(--success)' : 'var(--danger)',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {notification.message}
        </div>
      )}

      {/* Abnormal Warning Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          {alerts.map((al, index) => (
            <div key={index} style={{
              background: al.type === 'danger' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)',
              borderLeft: `4px solid ${al.type === 'danger' ? 'var(--danger)' : 'var(--warning)'}`,
              borderRadius: '8px',
              padding: '14px 20px',
              fontSize: '14px',
              color: al.type === 'danger' ? '#ef4444' : '#f59e0b',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
            }}>
              {al.message}
            </div>
          ))}
        </div>
      )}

      {/* Alert Center */}
      <AlertCenter
        requests={unlockRequests}
        assignments={assignments}
        subjects={subjects}
        classrooms={classrooms}
        teachers={teachers}
        onApprove={handleApproveUnlock}
        onReject={handleRejectUnlock}
      />

      {/* KPI Cards */}
      <KPIOverview
        totalSubjects={subjects.length}
        totalAssignments={assignments.length}
        totalStudents={totalStudentsCount}
        completionRate={compliance.rate}
        pendingUnlockRequests={pendingRequests.length}
      />

      {/* Visual Analytics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Left Side: Progress & Recents */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <ProgressOverview
            preSubmittedCount={preSubmittedCount}
            postSubmittedCount={postSubmittedCount}
            totalAssignments={assignments.length}
            overallProgress={compliance.rate}
          />

          {/* Recent reports / submissions list */}
          <div className="glassmorphism" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0' }}>📝 รายการส่งคะแนนล่าสุด</h3>
            {recentSubmissionsList.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center', padding: '24px' }}>📭 ยังไม่มีการบันทึกรายงานการส่งคะแนน</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {recentSubmissionsList.map((sub, index) => {
                  const assign = assignments.find(a => a.id === sub.teaching_assignment_id)
                  const room = assign ? getClassroomName(assign.classroom_id) : '—'
                  const subjectName = assign ? getSubjectInfo(assign.subject_id) : '—'
                  const teacherName = assign ? getTeacherName(assign.teacher_id) : '—'

                  return (
                    <div key={index} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '13px' }}>{subjectName} ({room})</strong>
                        <span style={{ fontSize: '11px', color: 'var(--success)', fontWeight: '600' }}>✓ ล็อกส่งแล้ว</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        <span>โดย: {teacherName}</span>
                        <span>วันที่: {formatDate(sub.updated_at || sub.pre_test_submitted_at || sub.post_test_submitted_at)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Donut Pie Chart & Bar Chart */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <SubmissionPieChart
            completeCount={pieStats.complete}
            inProgressCount={pieStats.inProgress}
            notStartedCount={pieStats.notStarted}
          />

          {/* Trend chart by HOD */}
          <div className="glassmorphism" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0' }}>📈 สัดส่วนคะแนนเฉลี่ยรายวิชาในกลุ่มสาระฯ</h3>
            {deptChartData.length > 0 ? (
              <div style={{ height: '220px', width: '100%', marginTop: '10px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={deptChartData}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" stroke="var(--text-secondary)" fontSize={11} tickLine={false} />
                    <YAxis unit="%" stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'var(--bg-card)', 
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)' 
                      }} 
                    />
                    <Legend verticalAlign="top" height={36} />
                    <Bar name="Pre-Test (%)" dataKey="prePercent" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar name="Post-Test (%)" dataKey="postPercent" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>📭 ยังไม่มีข้อมูลรายวิชาในขณะนี้</div>
            )}
          </div>
        </div>
      </div>

      {/* Exam Repository Analytics Block */}
      {compStats && (
        <div className="glassmorphism" style={{ padding: '28px', marginBottom: '24px', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} color="#6366f1" /> 📂 สถิติคลังข้อสอบกลุ่มสาระฯ (Exam Repository)
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: '2px 0 0' }}>
                ภาพรวมความคืบหน้าการสะสมข้อสอบของบุคลากรภายในกลุ่มสาระวิชา
              </p>
            </div>
            <Link to="/teacher/exam-repository" className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', borderColor: '#6366f1', color: '#6366f1' }}>
              เข้าสู่ระบบจัดการคลังข้อสอบ ➔
            </Link>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)', padding: '16px', borderRadius: '14px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>ข้อสอบสะสมทั้งหมด</span>
              <strong style={{ fontSize: '24px', color: '#6366f1' }}>{compStats.exams.totalFiles} ไฟล์</strong>
            </div>
            <div style={{ background: 'rgba(22, 163, 74, 0.04)', border: '1px solid rgba(22, 163, 74, 0.1)', padding: '16px', borderRadius: '14px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>เชื่อมโยง Google Drive</span>
              <strong style={{ fontSize: '24px', color: '#16a34a' }}>{compStats.exams.googleDriveFiles} ไฟล์</strong>
            </div>
            <div style={{ background: 'rgba(59, 130, 246, 0.04)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '16px', borderRadius: '14px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>อัปโหลดแบบ Local (Cloud)</span>
              <strong style={{ fontSize: '24px', color: 'var(--primary)' }}>{compStats.exams.localFiles} ไฟล์</strong>
            </div>
            <div style={{ background: 'rgba(245, 158, 11, 0.04)', border: '1px solid rgba(245, 158, 11, 0.1)', padding: '16px', borderRadius: '14px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>รายวิชาที่มีข้อสอบรองรับ</span>
              <strong style={{ fontSize: '24px', color: 'var(--warning)' }}>{compStats.exams.subjectsWithExams} / {subjects.length} วิชา</strong>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <div style={{ border: '1px solid var(--border)', padding: '20px', borderRadius: '14px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>🧑‍🏫 อัตราความร่วมมือของครูผู้สอน</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', marginBottom: '8px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>คุณครูที่ส่งข้อสอบแล้ว:</span>
                <strong style={{ color: 'var(--success)' }}>{compStats.exams.teachersUploadedCount} คน</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>คุณครูที่ยังไม่ส่งข้อสอบ:</span>
                <strong style={{ color: 'var(--danger)' }}>{compStats.exams.teachersNotUploadedCount} คน</strong>
              </div>
            </div>

            <div style={{ border: '1px solid var(--border)', padding: '20px', borderRadius: '14px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>🚨 รายชื่อคุณครูที่ยังไม่ได้นำส่งไฟล์ข้อสอบ</h3>
              {compStats.exams.teachersNotUploadedList.length === 0 ? (
                <p style={{ color: 'var(--success)', fontSize: '12px', margin: 0, fontWeight: '600' }}>✓ บุคลากรทุกคนในกลุ่มสาระฯ นำส่งครบถ้วนแล้ว</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {compStats.exams.teachersNotUploadedList.map((name: string, ni: number) => (
                    <span key={ni} style={{ background: 'rgba(239, 68, 68, 0.08)', color: 'var(--danger)', fontSize: '11px', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' }}>
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Teacher Summary Overview Card */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--text-primary)' }}>👨‍🏫 สรุปความคืบหน้าครูผู้สอนรายบุคคล</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: '2px 0 0' }}>
              อัตราการกรอกคะแนนสะสมแยกตามคุณครูผู้สอนทุกคนในกลุ่มสาระฯ
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowTeacherDetails(!showTeacherDetails)}
              className="btn btn-outline"
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '600'
              }}
            >
              {showTeacherDetails ? '👁️ ซ่อนข้อมูลรายบุคคล' : '👁️ แสดงข้อมูลรายบุคคล'}
            </button>
            <Link to="/department/classrooms" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px' }}>
              🏫 ตรวจสอบสถานะละเอียด →
            </Link>
          </div>
        </div>

        {/* Teacher Cards Grid or Compact Panel */}
        {showTeacherDetails ? (
          teacherSummaryList.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '18px'
            }}>
              {teacherSummaryList.map((t) => {
                const overallProgressPercent = Math.round((t.preRate + t.postRate) / 2)
                const initials = t.teacherName.split(' ').map((n: string) => n[0]).join('').substring(0, 2)
                
                return (
                  <div 
                    key={t.teacherId} 
                    className="glassmorphism hover-card-effect"
                    style={{
                      padding: '20px',
                      borderRadius: '16px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-card)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                  >
                    {/* Decorative glowing gradient border/dot for completion */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: t.allComplete ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                    }} />

                    {/* Header Row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: t.allComplete ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                        color: t.allComplete ? 'var(--success)' : 'var(--primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        border: `1px solid ${t.allComplete ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`
                      }}>
                        {initials || 'ครู'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '700', margin: 0, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.teacherName}
                        </h4>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                          📚 รับผิดชอบ {t.classroomsCount} ห้องเรียน
                        </span>
                      </div>
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: t.allComplete ? 'var(--success)' : 'var(--warning)',
                        boxShadow: `0 0 8px ${t.allComplete ? 'var(--success)' : 'var(--warning)'}`
                      }} title={t.allComplete ? 'ครบถ้วนแล้ว' : 'กำลังดำเนินการ'} />
                    </div>

                    {/* Progress Info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '18px' }}>
                      {/* Pre-Test Progress */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px', fontWeight: '500' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Pre-Test: {t.preComplete}/{t.classroomsCount} ห้อง</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{t.preRate}%</span>
                        </div>
                        <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${t.preRate}%`,
                            background: 'var(--primary)',
                            borderRadius: '2px'
                          }} />
                        </div>
                      </div>

                      {/* Post-Test Progress */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px', fontWeight: '500' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Post-Test: {t.postComplete}/{t.classroomsCount} ห้อง</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{t.postRate}%</span>
                        </div>
                        <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${t.postRate}%`,
                            background: 'var(--success)',
                            borderRadius: '2px'
                          }} />
                        </div>
                      </div>
                    </div>

                    {/* Action Link Footer */}
                    <div style={{
                      borderTop: '1px solid var(--border)',
                      paddingTop: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '12px'
                    }}>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        color: t.allComplete ? 'var(--success)' : 'var(--warning)'
                      }}>
                        {t.allComplete ? '✓ เสร็จสมบูรณ์' : `⚡️ คืบหน้า ${overallProgressPercent}%`}
                      </span>
                      <Link 
                        to={`/department/classrooms?search=${encodeURIComponent(t.teacherName.split(' ')[0])}`}
                        style={{
                          color: 'var(--primary)',
                          textDecoration: 'none',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                        className="hover-underline"
                      >
                        เช็กห้องเรียน →
                      </Link>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="glassmorphism" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              📭 ยังไม่มีข้อมูลครูผู้สอนในขณะนี้
            </div>
          )
        ) : (
          <div className="glassmorphism hover-card-effect" style={{
            padding: '16px 20px',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: 'var(--text-secondary)',
            cursor: 'pointer'
          }} onClick={() => setShowTeacherDetails(true)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>💡</span>
              <span>ตารางรายงานความคืบหน้าครูผู้สอนแต่ละท่านถูกซ่อนไว้เพื่อลดพื้นที่หน้าจอ</span>
            </div>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                fontWeight: '700',
                cursor: 'pointer',
                fontSize: '13px',
                padding: 0
              }}
            >
              แสดงข้อมูลรายบุคคล →
            </button>
          </div>
        )}
      </div>

      {/* Custom Summary Modal Dialog for HOD/Admin (PDPA Compliant - Aggregated only) */}
      {selectedSummaryAssignment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div className="glassmorphism" style={{
            width: '100%',
            maxWidth: '520px',
            borderRadius: '24px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            color: 'var(--text-primary)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px 24px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--primary)'
                }}>
                  <TrendingUp size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
                    สรุปความคืบหน้าการส่งคะแนน
                  </h3>
                  <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <CheckCircle2 size={12} /> ข้อมูลแบบสรุปรวม (PDPA Secured)
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedSummaryAssignment(null)}
                style={{
                  background: 'var(--bg-body)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  transition: '0.2s'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Classroom Context Info */}
              <div style={{
                background: 'var(--bg-body)',
                padding: '16px',
                borderRadius: '16px',
                border: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <BookOpen size={12} /> รายวิชา
                  </span>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginTop: '2px' }}>
                    {selectedSummaryAssignment.subjectCode} {selectedSummaryAssignment.subjectName}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Users size={12} /> ห้องเรียน
                  </span>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginTop: '2px' }}>
                    {selectedSummaryAssignment.classroomName}
                  </div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>ครูผู้สอน</span>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)', marginTop: '2px' }}>
                    {selectedSummaryAssignment.teacherName}
                  </div>
                </div>
              </div>

              {/* Progress Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Pre-Test Progress */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <BookOpen size={14} className="text-primary" /> การกรอก Pre-Test
                    </span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>
                      {selectedSummaryAssignment.preEnteredCount} / {selectedSummaryAssignment.totalStudents} คน
                    </span>
                  </div>
                  <div style={{ height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${(selectedSummaryAssignment.preEnteredCount / (selectedSummaryAssignment.totalStudents || 1)) * 100}%`,
                      background: 'linear-gradient(90deg, var(--primary), #818cf8)',
                      borderRadius: '4px'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px' }}>
                    <span style={{
                      color: selectedSummaryAssignment.preStatus === 'submitted' ? 'var(--success)' : 'var(--warning)',
                      fontWeight: '600'
                    }}>
                      สถานะ: {selectedSummaryAssignment.preStatus === 'submitted' ? 'ส่งคะแนนแล้ว' : 'รอดราฟต์'}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      คิดเป็น {Math.round((selectedSummaryAssignment.preEnteredCount / (selectedSummaryAssignment.totalStudents || 1)) * 100)}%
                    </span>
                  </div>
                </div>

                {/* Post-Test Progress */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Award size={14} className="text-success" /> การกรอก Post-Test
                    </span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>
                      {selectedSummaryAssignment.postEnteredCount} / {selectedSummaryAssignment.totalStudents} คน
                    </span>
                  </div>
                  <div style={{ height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${(selectedSummaryAssignment.postEnteredCount / (selectedSummaryAssignment.totalStudents || 1)) * 100}%`,
                      background: 'linear-gradient(90deg, var(--success), #34d399)',
                      borderRadius: '4px'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px' }}>
                    <span style={{
                      color: selectedSummaryAssignment.postStatus === 'submitted' ? 'var(--success)' : 'var(--warning)',
                      fontWeight: '600'
                    }}>
                      สถานะ: {selectedSummaryAssignment.postStatus === 'submitted' ? 'ส่งคะแนนแล้ว' : 'รอดราฟต์'}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      คิดเป็น {Math.round((selectedSummaryAssignment.postEnteredCount / (selectedSummaryAssignment.totalStudents || 1)) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Aggregated Score Analytics */}
              <div style={{
                background: 'var(--bg-body)',
                padding: '16px',
                borderRadius: '16px',
                border: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '12px',
                textAlign: 'center'
              }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>เฉลี่ย Pre-Test</span>
                  <div style={{ fontWeight: '800', fontSize: '18px', color: 'var(--text-primary)', marginTop: '4px' }}>
                    {selectedSummaryAssignment.preEnteredCount > 0 ? selectedSummaryAssignment.preAvg.toFixed(2) : '—'}
                  </div>
                  <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>เต็ม {selectedSummaryAssignment.maxPreScore}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>เฉลี่ย Post-Test</span>
                  <div style={{ fontWeight: '800', fontSize: '18px', color: 'var(--text-primary)', marginTop: '4px' }}>
                    {selectedSummaryAssignment.postEnteredCount > 0 ? selectedSummaryAssignment.postAvg.toFixed(2) : '—'}
                  </div>
                  <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>เต็ม {selectedSummaryAssignment.maxPostScore}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>พัฒนาการ (Gain)</span>
                  <div style={{ 
                    fontWeight: '800', 
                    fontSize: '18px', 
                    color: selectedSummaryAssignment.learningGain > 0 ? 'var(--success)' : selectedSummaryAssignment.learningGain < 0 ? 'var(--danger)' : 'var(--text-secondary)',
                    marginTop: '4px' 
                  }}>
                    {selectedSummaryAssignment.preEnteredCount > 0 && selectedSummaryAssignment.postEnteredCount > 0 
                      ? (selectedSummaryAssignment.learningGain > 0 ? '+' : '') + selectedSummaryAssignment.learningGain.toFixed(2) 
                      : '—'}
                  </div>
                  <span style={{ fontSize: '10px', color: selectedSummaryAssignment.learningGain > 0 ? 'var(--success)' : selectedSummaryAssignment.learningGain < 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
                    {selectedSummaryAssignment.learningGain > 0 ? 'ดีขึ้น' : selectedSummaryAssignment.learningGain < 0 ? 'ลดลง' : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                className="btn btn-primary"
                onClick={() => setSelectedSummaryAssignment(null)}
                style={{ padding: '8px 24px', borderRadius: '12px' }}
              >
                ตกลง, ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default DepartmentDashboard
